
import { defineComponent, PropType } from "vue";
import { previewReelScenery } from "@/api/ghlAi.api.js";

type PreviewOption = {
  optionId: string;
  pageUrl?: string;
  previewUrl?: string;
  selected?: boolean;
};

type ScenePreviewState = {
  loading: boolean;
  error: string;
  options: PreviewOption[];
  provider: string;
  refreshRemaining: number;
  requestKey: string;
};

type ReelScene = {
  id?: string;
  text?: string;
  imageKey?: string;
  stockQuery?: string | null;
  durationSec?: number;
  stockPickToken?: string;
  stockPickOptionId?: string;
};

const SCENE_TYPE_LABELS: Record<string, string> = {
  "broll-hook": "Opening",
  "broll-product": "Product moment",
  "broll-detail": "Detail",
  "broll-lifestyle": "Lifestyle",
  community: "Community clip",
  product: "Product spotlight"
};

function emptyPreviewState(): ScenePreviewState {
  return {
    loading: false,
    error: "",
    options: [],
    provider: "",
    refreshRemaining: 3,
    requestKey: ""
  };
}

export default defineComponent({
  name: "reel-scene-editor",
  props: {
    modelValue: {
      type: Array as PropType<ReelScene[]>,
      default: () => []
    },
    reelType: {
      type: String,
      default: "product"
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      previewByIndex: {} as Record<number, ScenePreviewState>,
      previewTimers: {} as Record<number, ReturnType<typeof setTimeout>>
    };
  },
  computed: {
    scenes(): ReelScene[] {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    totalDurationSec(): string {
      const total = this.scenes.reduce((sum, scene) => {
        const value = Number(scene.durationSec);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
      return total % 1 === 0 ? String(total) : total.toFixed(1);
    }
  },
  beforeUnmount() {
    Object.values(this.previewTimers).forEach((timer) => clearTimeout(timer));
  },
  methods: {
    sceneKey(scene: ReelScene, index: number) {
      return scene.id || `scene-${index}`;
    },
    previewState(index: number): ScenePreviewState {
      return this.previewByIndex[index] || emptyPreviewState();
    },
    sceneTypeLabel(imageKey?: string) {
      const key = String(imageKey || "").trim();
      return SCENE_TYPE_LABELS[key] || "Scene";
    },
    sceneryEditable(scene: ReelScene) {
      const key = String(scene.imageKey || "").trim();
      return key !== "community" && key !== "product";
    },
    sceneryLockedHint(scene: ReelScene) {
      const key = String(scene.imageKey || "").trim();
      if (key === "community") {
        return "Uses your selected community clip — no scenery keywords needed.";
      }
      if (key === "product") {
        return "Uses your product spotlight — no scenery keywords needed.";
      }
      return "";
    },
    formatDuration(value?: number) {
      const num = Number(value);
      if (!Number.isFinite(num)) return "4";
      return num % 1 === 0 ? String(num) : num.toFixed(1);
    },
    clampDuration(value: unknown) {
      const num = Number(value);
      if (!Number.isFinite(num)) return 4;
      return Math.max(2, Math.min(8, Math.round(num * 2) / 2));
    },
    buildPreviewRequestKey(scene: ReelScene) {
      return [
        String(scene.stockQuery || "").trim().replace(/\s+/g, " ").toLowerCase(),
        String(scene.text || "").trim().toLowerCase(),
        this.reelType
      ].join("|");
    },
    isOptionSelected(scene: ReelScene, optionId: string) {
      return String(scene.stockPickOptionId || "") === optionId;
    },
    setPreviewState(index: number, patch: Partial<ScenePreviewState>) {
      this.previewByIndex = {
        ...this.previewByIndex,
        [index]: {
          ...this.previewState(index),
          ...patch
        }
      };
    },
    updateScenes(next: ReelScene[]) {
      this.$emit("update:modelValue", next);
    },
    updateSceneField(index: number, field: keyof ReelScene, rawValue: string) {
      const next = this.scenes.map((scene, sceneIndex) => {
        if (sceneIndex !== index) return { ...scene };
        const updated = { ...scene };
        if (field === "durationSec") {
          updated.durationSec = this.clampDuration(rawValue);
        } else if (field === "stockQuery") {
          updated.stockQuery = rawValue;
        } else if (field === "text") {
          updated.text = rawValue;
        }
        return updated;
      });
      this.updateScenes(next);
    },
    clearScenePick(index: number) {
      const next = this.scenes.map((scene, sceneIndex) => {
        if (sceneIndex !== index) return { ...scene };
        const updated = { ...scene };
        delete updated.stockPickToken;
        delete updated.stockPickOptionId;
        return updated;
      });
      this.updateScenes(next);
    },
    providerAttribution(provider?: string) {
      const id = String(provider || "").trim().toLowerCase();
      if (id === "pexels") {
        return {
          prefix: "Clips provided by ",
          label: "Pexels",
          href: "https://www.pexels.com"
        };
      }
      if (id === "pixabay") {
        return {
          prefix: "Clips provided by ",
          label: "Pixabay",
          href: "https://pixabay.com"
        };
      }
      return null;
    },
    applyPreviewResult(index: number, data: any, requestKey: string) {
      const options = Array.isArray(data?.options) ? data.options : [];
      this.setPreviewState(index, {
        loading: false,
        error: "",
        options,
        provider: String(data?.provider || "").trim(),
        refreshRemaining: Number(data?.refreshRemaining ?? 0),
        requestKey
      });

      const next = this.scenes.map((scene, sceneIndex) => {
        if (sceneIndex !== index) return { ...scene };
        return {
          ...scene,
          stockPickToken: data?.pickToken || scene.stockPickToken,
          stockPickOptionId: data?.defaultOptionId || scene.stockPickOptionId
        };
      });
      this.updateScenes(next);
    },
    async fetchPreview(index: number, { refresh = false } = {}) {
      const scene = this.scenes[index];
      if (!scene || !this.sceneryEditable(scene) || this.disabled) return;

      const stockQuery = String(scene.stockQuery || "").trim().replace(/\s+/g, " ");
      if (stockQuery.split(/\s+/).filter(Boolean).length < 2) {
        this.setPreviewState(index, { ...emptyPreviewState(), requestKey: this.buildPreviewRequestKey(scene) });
        return;
      }

      const requestKey = this.buildPreviewRequestKey(scene);
      this.setPreviewState(index, {
        ...this.previewState(index),
        loading: true,
        error: "",
        requestKey
      });

      try {
        const payload: any = {
          stockQuery,
          sceneText: scene.text || "",
          reelType: this.reelType
        };
        if (refresh && scene.stockPickToken) {
          payload.pickToken = scene.stockPickToken;
          payload.refresh = true;
        } else if (scene.stockPickToken) {
          payload.pickToken = scene.stockPickToken;
        }

        const response = await previewReelScenery(payload);
        if (this.buildPreviewRequestKey(this.scenes[index]) !== requestKey) {
          return;
        }
        this.applyPreviewResult(index, response.data || {}, requestKey);
      } catch (e) {
        if (this.buildPreviewRequestKey(this.scenes[index]) !== requestKey) {
          return;
        }
        const err = e as { response?: { data?: { message?: string } } };
        this.setPreviewState(index, {
          loading: false,
          error: err?.response?.data?.message || "Unable to load background options.",
          options: [],
          requestKey
        });
      }
    },
    schedulePreview(index: number) {
      if (this.previewTimers[index]) {
        clearTimeout(this.previewTimers[index]);
      }
      this.previewTimers[index] = setTimeout(() => {
        this.fetchPreview(index);
      }, 800);
    },
    onSceneryInput(index: number, value: string) {
      const next = this.scenes.map((scene, sceneIndex) => {
        if (sceneIndex !== index) return { ...scene };
        const updated = { ...scene, stockQuery: value };
        delete updated.stockPickToken;
        delete updated.stockPickOptionId;
        return updated;
      });
      this.updateScenes(next);
      this.schedulePreview(index);
    },
    onCaptionInput(index: number, value: string) {
      this.updateSceneField(index, "text", value);
      const scene = this.scenes[index];
      if (scene?.stockQuery && String(scene.stockQuery).trim()) {
        this.schedulePreview(index);
      }
    },
    normalizeSceneryField(index: number) {
      const scene = this.scenes[index];
      if (!scene || !this.sceneryEditable(scene)) return;
      const trimmed = String(scene.stockQuery || "").trim().replace(/\s+/g, " ");
      if (trimmed === scene.stockQuery) return;
      this.onSceneryInput(index, trimmed);
    },
    clampDurationField(index: number) {
      const scene = this.scenes[index];
      if (!scene) return;
      const clamped = this.clampDuration(scene.durationSec);
      if (clamped === scene.durationSec) return;
      this.updateSceneField(index, "durationSec", String(clamped));
    },
    selectPreviewOption(index: number, optionId: string) {
      if (this.disabled) return;
      const next = this.scenes.map((scene, sceneIndex) => {
        if (sceneIndex !== index) return { ...scene };
        return { ...scene, stockPickOptionId: optionId };
      });
      this.updateScenes(next);
    },
    openPreviewPage(pageUrl?: string) {
      const url = String(pageUrl || "").trim();
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    refreshPreview(index: number) {
      this.fetchPreview(index, { refresh: true });
    }
  }
});
